<template>
  <div class="fpw">
    <header class="fpw-header">
      <div class="fpw-header-left">
        <img class="fpw-thumb" :src="fileIconSrc" :alt="kindLabel" />
        <div class="fpw-title">
          <h2 class="fpw-filename" :title="filename">{{ filename }}</h2>
          <div class="fpw-meta">
            <span class="fpw-chip">{{ kindLabel }}</span>
            <span v-if="sizeLabel" class="fpw-dot">·</span>
            <span v-if="sizeLabel">{{ sizeLabel }}</span>
            <span v-if="dateLabel" class="fpw-dot">·</span>
            <span v-if="dateLabel">{{ dateLabel }}</span>
          </div>
        </div>
      </div>

      <div class="fpw-actions">
        <a
          v-if="downloadUrl"
          class="fpw-icon-btn"
          :href="downloadUrl"
          :download="filename"
          :title="$t('page_2.file_preview.download')"
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 2v9"/><path d="M4 7l4 4 4-4"/><path d="M2 14h12"/>
          </svg>
        </a>
        <a
          v-if="webUrl"
          class="fpw-icon-btn"
          :href="webUrl"
          target="_blank"
          rel="noopener"
          :title="$t('page_2.file_preview.open_in_sharepoint')"
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 2h5v5"/><path d="M14 2L7 9"/><path d="M11 9v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4"/>
          </svg>
        </a>
        <a
          v-if="officeProto"
          class="fpw-btn fpw-btn--primary"
          :href="officeProto"
        >
          {{ $t('page_2.file_preview.edit_in_app') }}
        </a>
        <button
          type="button"
          class="fpw-icon-btn fpw-icon-btn--close"
          :title="$t('page_2.file_preview.close')"
          @click="closeWindow"
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
            <path d="M3 3l10 10M13 3L3 13"/>
          </svg>
        </button>
      </div>
    </header>

    <main class="fpw-main">
      <div v-if="initialLoading" class="fpw-overlay">
        <div class="fpw-spinner"></div>
        <div class="fpw-overlay-label">{{ $t('page_2.file_preview.loading_preview') }}</div>
      </div>

      <div v-else-if="fatalError" class="fpw-overlay">
        <svg class="fpw-overlay-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="9"/><path d="M12 7v5"/><circle cx="12" cy="16" r="0.6" fill="currentColor"/>
        </svg>
        <p class="fpw-overlay-title">{{ $t('page_2.file_preview.preview_failed') }}</p>
        <p class="fpw-overlay-sub">{{ fatalError }}</p>
      </div>

      <FilePreviewBody
        v-else-if="fileItem"
        :fileItem="fileItem"
        :groupId="groupId"
        :active="true"
      />
    </main>
  </div>
</template>

<script>
import moment from "moment";
import sharepoint from "@/apiCallers/promise_handler_db/sharepoint";
import {
  classifyFile,
  formatFileSize,
  officeProtocolUrl,
  getFileExtension,
} from "@/helpers/file";
import FilePreviewBody from "@/components/generalComponents/FileViewer/FilePreviewBody.vue";

const ICON_BY_EXT = {
  pdf: require("@/assets/img/pdf_icon.svg"),
  doc: require("@/assets/img/doc_icon.svg"),
  docx: require("@/assets/img/doc_icon.svg"),
  dotx: require("@/assets/img/doc_icon.svg"),
  dotm: require("@/assets/img/doc_icon.svg"),
  xls: require("@/assets/img/xls_icon.svg"),
  xlsx: require("@/assets/img/xls_icon.svg"),
  csv: require("@/assets/img/xls_icon.svg"),
  ppt: require("@/assets/img/ppt-icon.png"),
  pptx: require("@/assets/img/ppt-icon.png"),
  jpg: require("@/assets/img/jpg_icon.svg"),
  jpeg: require("@/assets/img/jpg_icon.svg"),
  png: require("@/assets/img/png_icon.svg"),
};
const DEFAULT_ICON = require("@/assets/img/dat_icon.svg");

export default {
  name: "FilePreviewWindow",
  components: { FilePreviewBody },
  data() {
    return {
      groupId: "",
      itemId: "",
      fileItem: null,
      initialLoading: true,
      fatalError: null,
    };
  },
  computed: {
    filename() {
      return (this.fileItem && this.fileItem.name) || "";
    },
    kind() {
      return classifyFile(this.fileItem);
    },
    kindLabel() {
      const map = {
        pdf: "PDF",
        image: "IMG",
        office: "DOC",
        text: "TXT",
        video: "VID",
        audio: "AUD",
        unsupported: "FILE",
      };
      return map[this.kind] || "FILE";
    },
    fileIconSrc() {
      if (!this.fileItem) return DEFAULT_ICON;
      const ext = getFileExtension(this.fileItem.name || "");
      return ICON_BY_EXT[ext] || DEFAULT_ICON;
    },
    downloadUrl() {
      return this.fileItem && this.fileItem["@microsoft.graph.downloadUrl"];
    },
    webUrl() {
      return this.fileItem && this.fileItem.webUrl;
    },
    officeProto() {
      return this.fileItem ? officeProtocolUrl(this.fileItem) : null;
    },
    sizeLabel() {
      return this.fileItem ? formatFileSize(this.fileItem.size) : "";
    },
    dateLabel() {
      const d = this.fileItem && this.fileItem.lastModifiedDateTime;
      if (!d) return "";
      const m = moment(d);
      return m.isValid() ? m.format("D. MMM YYYY.") : "";
    },
  },
  async created() {
    const { groupId, itemId } = this.$route.query;
    if (!groupId || !itemId) {
      this.fatalError = this.$t("page_2.file_preview.preview_failed");
      this.initialLoading = false;
      return;
    }
    this.groupId = String(groupId);
    this.itemId = String(itemId);
    try {
      const res = await sharepoint.getSingleDoc(null, this.groupId, this.itemId);
      this.fileItem = res && res.data ? res.data : null;
      if (!this.fileItem) throw new Error("Empty document response");
      document.title = this.fileItem.name || "Document preview";
    } catch (e) {
      console.error("[FilePreviewWindow] getSingleDoc failed:", e);
      this.fatalError = this.$t("page_2.file_preview.preview_failed");
    } finally {
      this.initialLoading = false;
    }
  },
  methods: {
    closeWindow() {
      window.close();
    },
  },
};
</script>

<style scoped>
.fpw {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 100vh;
  background: #fff;
  color: #2b2b41;
  font-family: "Barlow", sans-serif;
  overflow: hidden;
}

.fpw-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid rgba(43, 43, 65, 0.08);
  flex-shrink: 0;
}

.fpw-header-left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
  flex: 1 1 auto;
}

.fpw-thumb {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.fpw-title {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.fpw-filename {
  margin: 0 0 0.2rem 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.25;
  color: #2b2b41;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fpw-meta {
  display: flex;
  gap: 0.45rem;
  align-items: center;
  font-size: 0.82rem;
  color: #6b6b7d;
  font-weight: 500;
}

.fpw-chip {
  padding: 0.12rem 0.5rem;
  background: #eceef3;
  color: #3325AE;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.fpw-dot { color: #b8b8c1; }

.fpw-actions {
  display: flex;
  gap: 0.3rem;
  align-items: center;
  flex-shrink: 0;
}

.fpw-icon-btn {
  width: 2.2rem;
  height: 2.2rem;
  border: none;
  background: #eceef3;
  color: #2b2b41;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: background 160ms ease, color 160ms ease;
}
.fpw-icon-btn:hover {
  background: #fff;
  box-shadow: 0 2px 8px rgba(43, 43, 65, 0.12);
  color: #3325AE;
}
.fpw-icon-btn--close:hover {
  background: #fff;
  color: #d33;
}

.fpw-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: background 160ms ease, color 160ms ease, transform 160ms ease, box-shadow 160ms ease;
}
.fpw-btn--primary {
  background: #3325AE;
  color: #fff;
}
.fpw-btn--primary:hover {
  background: #2a1f8f;
  color: #fff;
  box-shadow: 0 6px 16px -4px rgba(51, 37, 174, 0.4);
  transform: translateY(-1px);
}

.fpw-main {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  background: #f3f7fc;
}

.fpw-main > * {
  flex: 1 1 auto;
  min-height: 0;
}

.fpw-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;
  padding: 2rem;
  background: #f3f7fc;
}
.fpw-overlay-icon {
  color: #b8b8c1;
}
.fpw-overlay-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #2b2b41;
  margin: 0;
}
.fpw-overlay-sub {
  font-size: 0.92rem;
  font-weight: 500;
  color: #6b6b7d;
  margin: 0;
}
.fpw-overlay-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #6b6b7d;
  letter-spacing: 0.04em;
}

.fpw-spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(51, 37, 174, 0.15);
  border-top-color: #3325AE;
  animation: fpw-spin 0.9s linear infinite;
}
@keyframes fpw-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  .fpw-header { padding: 0.7rem 0.85rem; }
  .fpw-filename { font-size: 0.95rem; }
  .fpw-icon-btn { width: 2rem; height: 2rem; }
}
</style>
